import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { isEqual, compareAsc, startOfDay } from "date-fns";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";
import { useDateRangeAction, DateRangeActionType } from "./useDateRangeAction";
import styles from "./DateRangePicker.scss";

export type DayButtonProps = {
  date: Date;
  isFirstDayOfMonth: boolean;
  isLastDayOfMonth: boolean;
  isFirstDayOfWeek: boolean;
  isLastDayOfWeek: boolean;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  previewKeyboardDate?: Date;
  previewDate?: Date;
  firstRender: boolean;
};

const defaultProps = {
  selectedStartDate: undefined,
  selectedEndDate: undefined,
  previewKeyboardDate: undefined,
  previewDate: undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  DayButtonProps,
  typeof defaultProps
> = defaultProps;

const DayButton: ((props: DayButtonProps) => JSX.Element) &
  ReactComponentStaticProps<DayButtonProps> = ({
  date,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isFirstDayOfWeek,
  isLastDayOfWeek,
  selectedStartDate,
  selectedEndDate,
  previewKeyboardDate,
  previewDate,
  firstRender,
}: DayButtonProps): JSX.Element => {
  const dispatch = useDateRangeAction();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isCurrentDate = isEqual(date, startOfDay(new Date()));

  const isDayRangeStart = !!(
    selectedStartDate && isEqual(date, selectedStartDate)
  );

  const isDaySelectingCursor = !!(previewDate && isEqual(date, previewDate));

  const isDayRangeEnd = !!(selectedEndDate && isEqual(date, selectedEndDate));

  const hasSelectedSingleDate = !!selectedStartDate && !selectedEndDate;

  const hasSelectedBothDates = !!selectedStartDate && !!selectedEndDate;

  const isSelectingSecondDate =
    !!selectedStartDate && !!previewDate && !selectedEndDate;

  const isSelectingCursorRightOfStart =
    !!isSelectingSecondDate &&
    compareAsc(selectedStartDate as Date, previewDate as Date) === 1;

  const isSelectingCursorLeftOfStart =
    isSelectingSecondDate &&
    compareAsc(selectedStartDate as Date, previewDate as Date) === -1;

  const shouldDisplayRangeStartLeftCover =
    isSelectingSecondDate &&
    isEqual(date, selectedStartDate as Date) &&
    isSelectingCursorRightOfStart;

  const shouldDisplayRangeStartRightCover =
    isSelectingSecondDate &&
    isEqual(date, selectedStartDate as Date) &&
    isSelectingCursorLeftOfStart;

  const shouldDisplaySelectingCursorLeftCover =
    isSelectingSecondDate &&
    isEqual(date, previewDate as Date) &&
    isSelectingCursorLeftOfStart;

  const shouldDisplaySelectingCursorRightCover =
    isSelectingSecondDate &&
    isEqual(date, previewDate as Date) &&
    isSelectingCursorRightOfStart;

  const isDayBetweenSelectedRange =
    hasSelectedBothDates &&
    compareAsc(selectedStartDate as Date, date) === -1 &&
    compareAsc(selectedEndDate as Date, date) === 1;

  const isDayBetweenSelectingRange =
    hasSelectedSingleDate &&
    previewDate &&
    ((compareAsc(selectedStartDate as Date, date) === -1 &&
      compareAsc(previewDate as Date, date) === 1) ||
      (compareAsc(selectedStartDate as Date, date) === 1 &&
        compareAsc(previewDate as Date, date) === -1));

  const generateLabel = (dayDate: Date) => {
    let isTodayString = "";
    let weekday = "";
    let dateString = "";
    let selectionTypeString = "";
    let selectedTypeString = "";

    isTodayString = isCurrentDate ? "Heute" : "";

    weekday = dayDate.toLocaleDateString("de-DE", {
      weekday: "long",
    });
    dateString = dayDate.toLocaleDateString("de-DE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    if (isDayRangeStart && isDayRangeEnd) {
      selectionTypeString = "als Beginn und Ende ausgewählt";
    } else if (isDayRangeStart) {
      selectionTypeString = "als Beginn ausgewählt";
    } else if (isDayRangeEnd) {
      selectionTypeString = "als Ende ausgewählt";
    }

    if (
      isDaySelectingCursor &&
      (!selectedStartDate || (selectedStartDate && selectedEndDate))
    ) {
      selectedTypeString = "als Beginn auswählen";
    } else if (selectedStartDate && !selectedEndDate && isDaySelectingCursor) {
      selectedTypeString = "als Ende auswählen";
    }

    return [
      isTodayString,
      weekday,
      dateString,
      selectionTypeString,
      selectedTypeString,
    ]
      .filter((str) => str)
      .join(", ");
  };

  useEffect(() => {
    if (
      !firstRender &&
      previewKeyboardDate &&
      isEqual(date, previewKeyboardDate)
    ) {
      buttonRef.current?.focus();
    }
  }, [previewKeyboardDate]);

  const handleCalendarKeyboardControls = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    // Space/Enter keys are handled by default behaviour (triggers click event)
    switch (e.key) {
      case "ArrowLeft":
        dispatch({ type: DateRangeActionType.DecrementKeyboardDateByDay });
        break;
      case "ArrowRight":
        dispatch({ type: DateRangeActionType.IncrementKeyboardDateByDay });
        break;
      case "ArrowUp":
        dispatch({ type: DateRangeActionType.DecrementKeyboardDateByWeek });
        break;
      case "ArrowDown":
        dispatch({ type: DateRangeActionType.IncrementKeyboardDateByWeek });
        break;
      case "PageUp":
        dispatch({ type: DateRangeActionType.DecrementKeyboardDateByMonth });
        break;
      case "PageDown":
        dispatch({ type: DateRangeActionType.IncrementKeyboardDateByMonth });
        break;
      case "Home":
        dispatch({ type: DateRangeActionType.SetKeyboardDateToMonthStart });
        break;
      case "End":
        dispatch({ type: DateRangeActionType.SetKeyboardDateToMonthEnd });
        break;
      case "Tab":
        dispatch({ type: DateRangeActionType.ResetPreviewDate });
        break;
      default:
        break;
    }
  };
  return (
    <button
      ref={buttonRef}
      type="button"
      className={cx(styles["day-button"], {
        [styles["day-button--range-start-selecting"]]:
          isDayRangeStart && hasSelectedSingleDate,
        [styles["day-button--cover-left"]]:
          shouldDisplayRangeStartLeftCover ||
          shouldDisplaySelectingCursorLeftCover,
        [styles["day-button--cover-right"]]:
          shouldDisplayRangeStartRightCover ||
          shouldDisplaySelectingCursorRightCover,
        [styles["day-button--range-start-selected"]]:
          hasSelectedBothDates && isDayRangeStart,
        [styles["day-button--range-end-selected"]]: isDayRangeEnd,
        [styles["day-button--cursor-selecting"]]: isDaySelectingCursor,
        [styles[
          "day-button--range-between-selected"
        ]]: isDayBetweenSelectedRange,
        [styles[
          "day-button--range-between-selecting"
        ]]: isDayBetweenSelectingRange,
        [styles["day-button--first-day-month"]]: isFirstDayOfMonth,
        [styles["day-button--last-day-month"]]: isLastDayOfMonth,
        [styles["day-button--first-day-week"]]: isFirstDayOfWeek,
        [styles["day-button--last-day-week"]]: isLastDayOfWeek,
      })}
      onClick={() =>
        dispatch({ type: DateRangeActionType.HandleRangeSelection, date })
      }
      onMouseEnter={() => {
        dispatch({
          type: DateRangeActionType.SetPreviewMouseDate,
          cursor: date,
        });
      }}
      onMouseLeave={() => {
        dispatch({
          type: DateRangeActionType.SetPreviewMouseDate,
          cursor: undefined,
        });
      }}
      tabIndex={
        previewKeyboardDate && isEqual(date, previewKeyboardDate) ? 0 : -1
      }
      onKeyDown={handleCalendarKeyboardControls}
      aria-label={generateLabel(date)}
    >
      {date.toLocaleDateString("de-DE", { day: "numeric" })}
      <span
        className={cx({
          [styles["day-button__current-date-circle"]]: isCurrentDate,
        })}
      />
    </button>
  );
};

DayButton.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  isFirstDayOfMonth: PropTypes.bool.isRequired,
  isLastDayOfMonth: PropTypes.bool.isRequired,
  isFirstDayOfWeek: PropTypes.bool.isRequired,
  isLastDayOfWeek: PropTypes.bool.isRequired,
  selectedStartDate: PropTypes.instanceOf(Date),
  selectedEndDate: PropTypes.instanceOf(Date),
  previewKeyboardDate: PropTypes.instanceOf(Date),
  previewDate: PropTypes.instanceOf(Date),
  firstRender: PropTypes.bool.isRequired,
};

DayButton.defaultProps = validatedDefaultProps;

DayButton.displayName = "DateRangePicker.DayButton";

export default DayButton;
